import { observable, action, runInAction, computed } from 'mobx';
import _ from 'lodash';
import { INode, INodesDestinationMeta, INodesDestination, ICrossReffernce } from '../../types/INode';
import { IDefaultDestination } from '../../types/IDefaultDestination';
import Api from '../../services/api';
import { ITranslatedNodes } from '../../types/ITranslatedNodes';
import { NodeTypes } from '../../types/NodeTypes';
import { DEFAULT_LANG } from '../constants';
import defaultDestinationsStore from '../DefaultDestinationsStore/DefaultDestinationsStore';
import queryParamsStore from '../QueryParamsStore/QueryParamsStore';
import uiContentsStore from '../UiContentsStore/UiContentsStore';
import { getTranslate, translateTemplate } from '../../utils/translate';
import UITranslations from '../uiTranslations';

export const NodeUtils = {
    getNodeDestinationPK(nodeId: any, destinationId: any) {
        return `${nodeId}-${destinationId}`
    },
    hasDestination(pkidOrId: string) {
        return pkidOrId?.includes("-")
    }
}
class NodesStore {
    @observable public nodes: INode[] = [];
    @observable public destinationNodes: INodesDestinationMeta[] = [];
    @observable public crossRefferences: ICrossReffernce[] = [];

    @action
    public getNodesByBuildingId = async (id: string | number | undefined) => {
        if (!id) {
            this.nodes = []
            return;
        }
        try {
            const nodes = await Api.getNodesByBuildingId(id.toString());
            const destinationNodes = await Api.getDestinationNodesByBuildingId(id.toString());
            const crossRefferences = await Api.getCrossRefferencesByBuildingId(id.toString())
            runInAction(() => {
                this.nodes = nodes;
                this.destinationNodes = destinationNodes;
                this.crossRefferences = crossRefferences;
            });
        } catch (e) {
            console.error(e);
        }
    };

    @action
    public translateNodes = (nodes: INode[], routeLang: string, defaultDestinations: IDefaultDestination[]) => {
        return _.map(nodes, (n: INode) => {
            let names = [] as any;
            let searchNames = [] as string[];
            _.each(n.destinations, (destination: INodesDestination) => {
                let defaultDestination: any =
                    defaultDestinations.find((defaultDestination: IDefaultDestination) => defaultDestination.id === destination.destinationId);
                defaultDestination.defaultDestinationNames = (_.keyBy(defaultDestination.defaultDestinationNames, 'lang'));
                const nameToBePushed = defaultDestination.defaultDestinationNames[routeLang] ? defaultDestination.defaultDestinationNames[routeLang].name : n.id

                names.push(nameToBePushed)

                const ref = this.destinationNodes.find(x => x.destinationId === destination.destinationId)
                searchNames.push(
                    ...(ref?.keywords[routeLang] ?? []),
                    nameToBePushed
                )
                destination.destinationId
            });


            const name = _.join(names, ', ')
            const searchTerm = _.join(searchNames, ', ')
            return { ...n, name, searchTerm } as ITranslatedNodes
        });
    }

    @action
    public translateNodeDestinations = (nodes: INode[], routeLang: string, defaultDestinations: IDefaultDestination[]) => {
        return _.flatMap(nodes, (n: INode) => {
            if (n.nodeTypeId === NodeTypes.Junction) {
                const uiContents = uiContentsStore.uiContents;
                let template = getTranslate(uiContents, routeLang)(UITranslations.PhotoRoutePage.Location.id);
                if (template.includes('missing translation')) {
                    template = UITranslations.PhotoRoutePage.Location.default
                }
                const translation = translateTemplate(template, { nodeId: n.id });

                return [{...n, name: translation, pkid: n.id.toString()} as any] as ITranslatedNodes[]
            }
            
            let nodes = [] as ITranslatedNodes[];
            _.each(n.destinations, (destination: INodesDestination) => {
                let defaultDestination: any =
                    defaultDestinations.find((defaultDestination: IDefaultDestination) => defaultDestination.id === destination.destinationId);
                defaultDestination.defaultDestinationNames = (_.keyBy(defaultDestination.defaultDestinationNames, 'lang'));
                const nameToBePushed = defaultDestination.defaultDestinationNames[routeLang] ? defaultDestination.defaultDestinationNames[routeLang].name : n.id


                const ref = this.destinationNodes.find(x => x.destinationId === destination.destinationId)                
                const searchNamestoBePushed = [
                    ...(ref?.keywords[routeLang] ?? []),
                    nameToBePushed
                ]
                const searchTerm = searchNamestoBePushed.join(", ")

                // names.push(nameToBePushed)
                // searchNames.push(...searchNamestoBePushed)
                nodes.push({ 
                    ...n,
                    name: nameToBePushed,
                    searchTerm: searchTerm,
                    destinationId: destination.destinationId,
                    pkid: NodeUtils.getNodeDestinationPK(n.id, destination.destinationId),
                    closed: ref && ref.closedNodeIds.includes(n.id),
                } as any)
                destination.destinationId
            });

            return nodes
        });
    }

    findNodeViaString(pkidOrId: string) {
        if (NodeUtils.hasDestination(pkidOrId)) {
            return this.translatedNodes.find(x => x.pkid === pkidOrId)
        } else {
            return this.translatedNodes.find(x => x.id.toString() === pkidOrId)
        }
    }

    @computed get translatedNodes() {
        const { defaultDestinations } = defaultDestinationsStore;
        const { queryParams, setQueryParam } = queryParamsStore;


        if (!queryParams || defaultDestinations.length === 0 || !this.nodes) return [] as ITranslatedNodes[];

        const routeLang = queryParams.routeLang || DEFAULT_LANG;
        let translatedNodes = _.pickBy(this.nodes, 
            n => (
                n.nodeTypeId === NodeTypes.NodesWithDestinations ||
                n.nodeTypeId === NodeTypes.Junction
            ) && !n.hide
        );
        translatedNodes = this.translateNodeDestinations(translatedNodes as any, routeLang, defaultDestinations);
        translatedNodes = _.orderBy(translatedNodes, ['popular', 'name'], ['desc', 'asc']);
        return translatedNodes as ITranslatedNodes[];
    }
}

const nodesStore = new NodesStore();

export default nodesStore;
